import { ChangeEvent, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Input } from '@opendoor/bricks/core';

import { NumberQuestion } from 'src/lib/flow/FlowQuestion';
import { errorAnswer } from 'src/store/sellerFlow/actions';
import { changeAnswers } from 'src/store/sellerInput';

import QuestionLabel from '../common/QuestionLabel';
import { trackAnswerChange } from './trackAnswerChange';

type NumberInputProps = {
  question: NumberQuestion;
  value?: string;
};

const NumberInput: React.FC<NumberInputProps> = ({ question, value }) => {
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const { key, placeholder, label, helperText, maxValue, minValue } = question;

  const onChange = (_event: ChangeEvent<HTMLInputElement>, newValue: string | undefined) => {
    let answer = newValue ? parseInt(newValue, 10) : null;

    let errorMessage: string | undefined;
    if (typeof answer === 'number' && !Number.isNaN(answer)) {
      if (maxValue && answer > maxValue) {
        errorMessage = `Can't be greater than ${maxValue}`;
      } else if (minValue && answer < minValue) {
        errorMessage = `Can't be less than ${minValue}`;
      }
    } else {
      answer = null;
    }

    if (errorMessage) {
      setErrorMessage(errorMessage);
      dispatch(errorAnswer(key, true, errorMessage));
    } else {
      // clear our previous error message
      setErrorMessage(undefined);
      dispatch(errorAnswer(key, false));
    }

    dispatch(changeAnswers([{ key, answer }]));
    trackAnswerChange(key, newValue ?? undefined);
  };

  // `value` - is our initialValue, but for numbers we want to allow the user to delete a prefill
  // this.state.shouldOverridePrefillWithUndefined ? undefined : answer;
  // we may need to still check that the user has not manually cleared out an input
  // so that we do not show the answer prefill
  return (
    <Input
      analyticsName={`reception-fe-question-${question.label}`}
      decimalPlaces={0}
      errorMessage={errorMessage}
      invalid={!!errorMessage}
      fontSize="s0"
      id={`integer-${key}`}
      // this makes it so the * for required inputs shows next to the label instead of below
      labelStyles={{ display: 'flex' }}
      label={<QuestionLabel unboundedWidth={true} title={label} />}
      max={question.maxValue}
      min={question.minValue}
      name={key}
      onBlur={() => trackAnswerChange(key, value)}
      onChange={onChange}
      placeholder={placeholder}
      required={!question.optional}
      type="number"
      unboundedWidth
      value={value ? value.toString() : undefined}
      helperText={helperText}
    />
  );
};

export default NumberInput;
